import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "../../../redux/hooks";
import { resolveScopedVaultApi } from "../../../api/vault.api";
import { vaultKeys } from "../queryKeys";
import { setActiveRequirement } from "../../../redux/slices/vaultSlice";
import { setActiveRequirement as setAdminActiveRequirement } from "../../../redux/slices/requirementsSlice";
import { getExceptionMessage } from "../../../exceptions/helpers/getExceptionMessage";
import { vaultExceptions } from "../../../exceptions/default/vaultExceptions";
import { VaultFormValidationError } from "../../../exceptions/VaultFormValidationError";
import { confirmUploadSubmissionBestEffort } from "../../../utils/vault/resumableUpload";
import { useUploadFileToVault } from "./useUploadFileToVault";
import { getVaultUploadRecoveryContext } from "../../../utils/vault/vaultActorSession";
import { withVaultUploadRecoveryLease } from "../../../utils/vault/uploadRecoveryLease";
import type { UploadPolicy } from "../../../constants/uploadPolicies";

export function useUploadIdFiles(folderId: string | undefined) {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const uploadMutation = useUploadFileToVault(folderId);
  const scopedVaultApi = resolveScopedVaultApi(
    typeof window !== "undefined" ? window.location.pathname : "",
  );

  return useMutation({
    mutationFn: async ({
      reqId,
      formData,
      uploadPolicy,
      newFormValues,
      newStatus,
      uploadBatchAction,
      uploadBatchActionOnSubmit,
    }: {
      reqId: string;
      formData: FormData;
      uploadPolicy: UploadPolicy;
      newFormValues?: any[];
      newStatus?: string;
      uploadBatchAction?: "replace_evidence";
      uploadBatchActionOnSubmit?: "replace_evidence_on_submit";
    }) => {
      const { scope: recoveryScope, ownerId } = getVaultUploadRecoveryContext();
      return withVaultUploadRecoveryLease({
        requirementId: reqId,
        recoveryScope,
        ownerId,
        operation: async () => {
          // Step 1: Upload files
          let uploadBatchId: string | undefined;
          await uploadMutation.mutateAsync({
            reqId,
            formData,
            uploadPolicy,
            skipStatusUpdate: true,
            uploadBatchAction,
            onResumableFinalized: (batchId) => {
              uploadBatchId = batchId;
            },
          });
          // Step 2: Update form values if provided
          if (newFormValues || newStatus || uploadBatchId) {
            let updated;
            try {
              updated = await scopedVaultApi.updateFormValues(reqId, {
                form_values: newFormValues ?? null,
                ...(newStatus || uploadBatchId
                  ? { status: newStatus ?? "pending_review" }
                  : {}),
                ...(uploadBatchId ? { upload_batch_id: uploadBatchId } : {}),
                ...(uploadBatchId &&
                (uploadBatchActionOnSubmit || uploadBatchAction)
                  ? {
                      upload_batch_action:
                        uploadBatchActionOnSubmit || uploadBatchAction,
                    }
                  : {}),
              });
            } catch (error: any) {
              // Same mapping as useUpdateVaultFormValues: surface backend
              // field-level rejections (RV001) and coded errors in Spanish
              // instead of a raw axios message.
              const data = error.response?.data;
              if (data?.code === "RV001" && data?.fields) {
                throw new VaultFormValidationError(
                  getExceptionMessage(
                    data.code,
                    vaultExceptions.updateVaultFormValues,
                  ),
                  data.fields as Record<string, string>,
                );
              }
              // RT001 parity with useUpdateVaultFormValues: the requirement was
              // approved while the page was open — refetch so the UI locks
              // itself (the RT001 copy promises "la página se actualizará").
              if (data?.code === "RT001" && folderId) {
                await queryClient.invalidateQueries({
                  queryKey: vaultKeys.folder(folderId),
                });
              }
              const backendMessage = data?.error ?? data?.detail;
              if (
                typeof backendMessage === "string" &&
                (String(data?.code ?? "").startsWith("UPLOAD_") ||
                  String(data?.code ?? "").startsWith("V"))
              ) {
                throw new Error(backendMessage);
              }
              throw new Error(
                getExceptionMessage(
                  data?.code,
                  vaultExceptions.updateVaultFormValues,
                ),
              );
            }
            if (uploadBatchId) {
              await confirmUploadSubmissionBestEffort(
                scopedVaultApi,
                reqId,
                uploadBatchId,
                undefined,
                recoveryScope,
              );
            }
            return updated;
          }
          return null;
        },
      });
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: vaultKeys.folder(folderId ?? ""),
      });
      if (data) {
        dispatch(setActiveRequirement(data));
        dispatch(setAdminActiveRequirement(data));
      }
    },
  });
}
